// src/components/circles/useCircleDialogFocus.ts
//
// Keyboard behaviour for the Circle dialogs that are hand-rolled overlays
// rather than the shared Modal: focus moves into the dialog, Tab stays inside
// it (useFocusTrap, as CirclesPage's New Circle dialog does), Escape closes
// it, and focus returns to whatever opened it.
//
// The dialogs are mounted only while open, so "open" is the hook's lifetime.
// The opener is captured in a state initializer — during the first render —
// because an autoFocus inside the dialog runs at commit, before effects, and
// an effect would record that field instead of the opener.

import { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export function useCircleDialogFocus<T extends HTMLElement = HTMLDivElement>(
  onClose: () => void,
  busy = false,
) {
  const ref = useRef<T>(null);
  useFocusTrap(ref, true);

  const [opener] = useState<HTMLElement | null>(
    () => (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null),
  );
  useEffect(() => () => opener?.focus?.(), [opener]);

  // Move focus in, unless something inside (an autoFocus field) already has it.
  useEffect(() => {
    const el = ref.current;
    if (el && !el.contains(document.activeElement)) el.focus();
  }, []);

  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };
  useEffect(() => {
    // Capture phase on window, and the Escape stops there: these dialogs can
    // sit over a slide-over that also closes on Escape, and one key press
    // must close only the topmost layer.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!latest.current.busy) latest.current.onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return ref;
}
